/**
 * CSS Framework Detector
 *
 * Auto-detects the CSS framework being used (Bootstrap 4, Bootstrap 5, or Tailwind CSS)
 * and provides appropriate class mappings for UI components.
 *
 * Usage:
 *   import { detectFramework, getFrameworkClasses, FrameworkDetector } from './framework-detector';
 *
 *   // Get detected framework
 *   const framework = detectFramework(); // 'bootstrap-4', 'bootstrap-5', 'tailwind', or 'unknown'
 *
 *   // Get classes for a component
 *   const classes = getFrameworkClasses('alert', 'success');
 *   // Returns: 'alert alert-success' (Bootstrap) or 'p-4 mb-4 text-sm text-green-800 bg-green-50 rounded-lg' (Tailwind)
 */

import { is_bootstrap_version_4, is_bootstrap_version_5 } from './bootstrap_version';

/**
 * Framework detection cache
 */
let detectedFramework = null;

/**
 * Framework constants
 */
export const FRAMEWORK = {
    BOOTSTRAP_4: 'bootstrap-4',
    BOOTSTRAP_5: 'bootstrap-5',
    TAILWIND: 'tailwind',
    UNKNOWN: 'unknown',
};

/**
 * Detect if Tailwind CSS is loaded
 * @returns {boolean}
 */
const hasTailwind = () => {
    // Check for Tailwind-specific utilities in document
    if (typeof document === 'undefined') return false;

    // Method 1: Check for common Tailwind utility classes in stylesheets
    try {
        for (const sheet of document.styleSheets) {
            try {
                const rules = sheet.cssRules || sheet.rules;
                if (!rules) continue;

                for (const rule of rules) {
                    // Look for Tailwind-specific patterns
                    if (rule.selectorText) {
                        // Tailwind uses classes like .bg-blue-500, .text-sm, .flex, .p-4
                        if (/\.(bg|text|p|m|flex|grid|w|h)-/.test(rule.selectorText)) {
                            return true;
                        }
                    }
                }
            } catch (e) {
                // Cross-origin stylesheet, skip
                continue;
            }
        }
    } catch (e) {
        // Stylesheet access error
    }

    // Method 2: Check for Tailwind config on window
    if (typeof window !== 'undefined' && window.tailwind) {
        return true;
    }

    // Method 3: Check for elements using common Tailwind classes
    const tailwindIndicators = [
        '[class*="bg-blue-"]',
        '[class*="text-gray-"]',
        '[class*="rounded-lg"]',
        '[class*="shadow-md"]',
        '.flex.items-center',
        '.grid.grid-cols-',
    ];

    for (const selector of tailwindIndicators) {
        try {
            if (document.querySelector(selector)) {
                return true;
            }
        } catch (e) {
            // Invalid selector
        }
    }

    // Method 4: Check for data attribute marker (can be set by app)
    if (document.documentElement.dataset.cssFramework === 'tailwind') {
        return true;
    }

    return false;
};

/**
 * Detect the CSS framework being used
 * @param {boolean} [force=false] - Force re-detection
 * @returns {string} Framework identifier
 */
export const detectFramework = (force = false) => {
    if (detectedFramework && !force) {
        return detectedFramework;
    }

    // Check for explicit framework setting
    if (typeof document !== 'undefined') {
        const explicit = document.documentElement.dataset.cssFramework;
        if (explicit) {
            detectedFramework = explicit;
            return detectedFramework;
        }
    }

    // Check Bootstrap versions using existing utility
    if (is_bootstrap_version_5()) {
        detectedFramework = FRAMEWORK.BOOTSTRAP_5;
        return detectedFramework;
    }

    if (is_bootstrap_version_4()) {
        detectedFramework = FRAMEWORK.BOOTSTRAP_4;
        return detectedFramework;
    }

    // Check for Tailwind
    if (hasTailwind()) {
        detectedFramework = FRAMEWORK.TAILWIND;
        return detectedFramework;
    }

    detectedFramework = FRAMEWORK.UNKNOWN;
    return detectedFramework;
};

/**
 * Bootstrap class mappings
 */
const bootstrapClasses = {
    // Alerts
    alert: {
        base: 'alert',
        success: 'alert-success',
        error: 'alert-danger',
        danger: 'alert-danger',
        warning: 'alert-warning',
        info: 'alert-info',
        dismissible: 'alert-dismissible fade show',
    },

    // Buttons
    button: {
        base: 'btn',
        primary: 'btn-primary',
        secondary: 'btn-secondary',
        success: 'btn-success',
        danger: 'btn-danger',
        warning: 'btn-warning',
        info: 'btn-info',
        light: 'btn-light',
        dark: 'btn-dark',
        link: 'btn-link',
        sm: 'btn-sm',
        lg: 'btn-lg',
        block: 'btn-block', // BS4 only, BS5 uses d-grid
        loading: 'disabled',
    },

    // Forms
    form: {
        group: 'form-group', // BS4: form-group, BS5: mb-3
        control: 'form-control',
        label: 'form-label', // BS5 only
        check: 'form-check',
        checkInput: 'form-check-input',
        checkLabel: 'form-check-label',
        select: 'form-select', // BS5 only, BS4 uses form-control
        invalid: 'is-invalid',
        valid: 'is-valid',
        feedback: 'invalid-feedback',
        text: 'form-text',
    },

    // Modal
    modal: {
        base: 'modal',
        dialog: 'modal-dialog',
        content: 'modal-content',
        header: 'modal-header',
        title: 'modal-title',
        body: 'modal-body',
        footer: 'modal-footer',
        sm: 'modal-sm',
        lg: 'modal-lg',
        xl: 'modal-xl',
        centered: 'modal-dialog-centered',
        scrollable: 'modal-dialog-scrollable',
    },

    // Spinner
    spinner: {
        border: 'spinner-border',
        grow: 'spinner-grow',
        sm: 'spinner-border-sm',
    },

    // Text
    text: {
        muted: 'text-muted',
        primary: 'text-primary',
        success: 'text-success',
        danger: 'text-danger',
        warning: 'text-warning',
        info: 'text-info',
        center: 'text-center',
        start: 'text-start', // BS5, BS4 uses text-left
        end: 'text-end', // BS5, BS4 uses text-right
    },

    // Layout
    layout: {
        container: 'container',
        containerFluid: 'container-fluid',
        row: 'row',
        col: 'col',
        dFlex: 'd-flex',
        dNone: 'd-none',
        dBlock: 'd-block',
        justifyCenter: 'justify-content-center',
        alignCenter: 'align-items-center',
    },

    // Spacing (same in BS4/BS5)
    spacing: {
        m0: 'm-0',
        m1: 'm-1',
        m2: 'm-2',
        m3: 'm-3',
        m4: 'm-4',
        m5: 'm-5',
        mt3: 'mt-3',
        mb3: 'mb-3',
        p3: 'p-3',
    },
};

/**
 * Bootstrap 5 specific overrides
 */
const bootstrap5Overrides = {
    form: {
        group: 'mb-3',
        select: 'form-select',
    },
    button: {
        block: 'd-grid gap-2', // BS5 uses d-grid instead of btn-block
    },
    text: {
        start: 'text-start',
        end: 'text-end',
    },
};

/**
 * Bootstrap 4 specific overrides
 */
const bootstrap4Overrides = {
    form: {
        group: 'form-group',
        select: 'form-control', // BS4 doesn't have form-select
        label: '', // BS4 doesn't need special label class
    },
    text: {
        start: 'text-left',
        end: 'text-right',
    },
};

/**
 * Tailwind CSS class mappings
 */
const tailwindClasses = {
    // Alerts
    alert: {
        base: 'p-4 mb-4 text-sm rounded-lg',
        success: 'text-green-800 bg-green-50 dark:bg-gray-800 dark:text-green-400',
        error: 'text-red-800 bg-red-50 dark:bg-gray-800 dark:text-red-400',
        danger: 'text-red-800 bg-red-50 dark:bg-gray-800 dark:text-red-400',
        warning: 'text-yellow-800 bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300',
        info: 'text-blue-800 bg-blue-50 dark:bg-gray-800 dark:text-blue-400',
        dismissible: 'relative',
    },

    // Buttons
    button: {
        base: 'font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none focus:ring-4',
        primary: 'text-white bg-blue-700 hover:bg-blue-800 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800',
        secondary: 'text-gray-900 bg-white border border-gray-300 hover:bg-gray-100 focus:ring-gray-100 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700',
        success: 'text-white bg-green-700 hover:bg-green-800 focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800',
        danger: 'text-white bg-red-700 hover:bg-red-800 focus:ring-red-300 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-900',
        warning: 'text-white bg-yellow-400 hover:bg-yellow-500 focus:ring-yellow-300 dark:focus:ring-yellow-900',
        info: 'text-white bg-blue-400 hover:bg-blue-500 focus:ring-blue-300 dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:ring-blue-700',
        light: 'text-gray-900 bg-white border border-gray-300 hover:bg-gray-100 focus:ring-gray-100',
        dark: 'text-white bg-gray-800 hover:bg-gray-900 focus:ring-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700',
        link: 'text-blue-600 hover:underline',
        sm: 'px-3 py-2 text-xs',
        lg: 'px-6 py-3 text-base',
        block: 'w-full',
        loading: 'opacity-50 cursor-not-allowed',
    },

    // Forms
    form: {
        group: 'mb-4',
        control: 'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500',
        label: 'block mb-2 text-sm font-medium text-gray-900 dark:text-white',
        check: 'flex items-center',
        checkInput: 'w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600',
        checkLabel: 'ml-2 text-sm font-medium text-gray-900 dark:text-gray-300',
        select: 'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500',
        invalid: 'border-red-500 focus:ring-red-500 focus:border-red-500',
        valid: 'border-green-500 focus:ring-green-500 focus:border-green-500',
        feedback: 'mt-2 text-sm text-red-600 dark:text-red-500',
        text: 'mt-2 text-sm text-gray-500 dark:text-gray-400',
    },

    // Modal
    modal: {
        base: 'fixed inset-0 z-50 overflow-y-auto',
        overlay: 'fixed inset-0 bg-black bg-opacity-50 transition-opacity',
        dialog: 'relative bg-white rounded-lg shadow dark:bg-gray-700 mx-auto mt-20',
        content: '',
        header: 'flex items-center justify-between p-4 border-b rounded-t dark:border-gray-600',
        title: 'text-xl font-semibold text-gray-900 dark:text-white',
        body: 'p-6',
        footer: 'flex items-center p-6 space-x-2 border-t border-gray-200 rounded-b dark:border-gray-600',
        sm: 'max-w-md',
        lg: 'max-w-4xl',
        xl: 'max-w-7xl',
        centered: 'flex items-center justify-center min-h-screen',
        scrollable: 'max-h-screen overflow-y-auto',
    },

    // Spinner
    spinner: {
        border: 'animate-spin rounded-full border-4 border-gray-200 border-t-blue-600',
        grow: 'animate-pulse rounded-full bg-blue-600',
        sm: 'w-4 h-4',
        md: 'w-8 h-8',
        lg: 'w-12 h-12',
    },

    // Text
    text: {
        muted: 'text-gray-500 dark:text-gray-400',
        primary: 'text-blue-600 dark:text-blue-500',
        success: 'text-green-600 dark:text-green-500',
        danger: 'text-red-600 dark:text-red-500',
        warning: 'text-yellow-600 dark:text-yellow-500',
        info: 'text-blue-600 dark:text-blue-500',
        center: 'text-center',
        start: 'text-left',
        end: 'text-right',
    },

    // Layout
    layout: {
        container: 'container mx-auto px-4',
        containerFluid: 'w-full px-4',
        row: 'flex flex-wrap -mx-4',
        col: 'px-4',
        dFlex: 'flex',
        dNone: 'hidden',
        dBlock: 'block',
        justifyCenter: 'justify-center',
        alignCenter: 'items-center',
    },

    // Spacing
    spacing: {
        m0: 'm-0',
        m1: 'm-1',
        m2: 'm-2',
        m3: 'm-3',
        m4: 'm-4',
        m5: 'm-5',
        mt3: 'mt-3',
        mb3: 'mb-3',
        p3: 'p-3',
    },
};

/**
 * Get classes for a component based on detected framework
 * @param {string} component - Component type (alert, button, form, etc.)
 * @param {string|string[]} variant - Variant or array of variants
 * @param {string} [framework] - Override detected framework
 * @returns {string} CSS classes
 */
export const getFrameworkClasses = (component, variant, framework = null) => {
    const fw = framework || detectFramework();
    let classMap;

    switch (fw) {
        case FRAMEWORK.BOOTSTRAP_5:
            classMap = mergeDeep({}, bootstrapClasses, bootstrap5Overrides);
            break;
        case FRAMEWORK.BOOTSTRAP_4:
            classMap = mergeDeep({}, bootstrapClasses, bootstrap4Overrides);
            break;
        case FRAMEWORK.TAILWIND:
            classMap = tailwindClasses;
            break;
        default:
            // Default to Bootstrap 5 classes
            classMap = mergeDeep({}, bootstrapClasses, bootstrap5Overrides);
    }

    const componentClasses = classMap[component];
    if (!componentClasses) {
        return '';
    }

    // Handle array of variants
    if (Array.isArray(variant)) {
        const classes = [];
        if (componentClasses.base) {
            classes.push(componentClasses.base);
        }
        variant.forEach(v => {
            if (componentClasses[v]) {
                classes.push(componentClasses[v]);
            }
        });
        return classes.join(' ');
    }

    // Single variant
    const base = componentClasses.base || '';
    const variantClass = componentClasses[variant] || '';

    return [base, variantClass].filter(Boolean).join(' ');
};

/**
 * Deep merge utility
 * @param {Object} target - Target object
 * @param {...Object} sources - Source objects
 * @returns {Object}
 */
const mergeDeep = (target, ...sources) => {
    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }

    return mergeDeep(target, ...sources);
};

/**
 * Check if value is an object
 * @param {*} item
 * @returns {boolean}
 */
const isObject = (item) => {
    return item && typeof item === 'object' && !Array.isArray(item);
};

/**
 * Framework Detector object for advanced usage
 */
export const FrameworkDetector = {
    /**
     * Detected framework (cached)
     */
    get current() {
        return detectFramework();
    },

    /**
     * Force re-detection
     */
    refresh() {
        return detectFramework(true);
    },

    /**
     * Check if Bootstrap (any version)
     */
    get isBootstrap() {
        const fw = detectFramework();
        return fw === FRAMEWORK.BOOTSTRAP_4 || fw === FRAMEWORK.BOOTSTRAP_5;
    },

    /**
     * Check if Bootstrap 4
     */
    get isBootstrap4() {
        return detectFramework() === FRAMEWORK.BOOTSTRAP_4;
    },

    /**
     * Check if Bootstrap 5
     */
    get isBootstrap5() {
        return detectFramework() === FRAMEWORK.BOOTSTRAP_5;
    },

    /**
     * Check if Tailwind
     */
    get isTailwind() {
        return detectFramework() === FRAMEWORK.TAILWIND;
    },

    /**
     * Manually set framework
     * @param {string} framework - Framework identifier
     */
    set(framework) {
        if (Object.values(FRAMEWORK).includes(framework)) {
            detectedFramework = framework;
            if (typeof document !== 'undefined') {
                document.documentElement.dataset.cssFramework = framework;
            }
        }
    },

    /**
     * Get classes for component
     */
    classes: getFrameworkClasses,

    /**
     * Available frameworks
     */
    FRAMEWORK,

    /**
     * Get all class mappings for current framework
     */
    get classMappings() {
        const fw = detectFramework();
        switch (fw) {
            case FRAMEWORK.BOOTSTRAP_5:
                return mergeDeep({}, bootstrapClasses, bootstrap5Overrides);
            case FRAMEWORK.BOOTSTRAP_4:
                return mergeDeep({}, bootstrapClasses, bootstrap4Overrides);
            case FRAMEWORK.TAILWIND:
                return tailwindClasses;
            default:
                return bootstrapClasses;
        }
    },
};

export default FrameworkDetector;
